import Text from "common/Typography/Text.tsx";
import Title from "common/Typography/Title.tsx";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import { ICON_TYPES } from "@shared/common/constants.ts";
import Flex from "common/Boxes/Flex.tsx";
import Modal from "common/Modal/Modal.tsx";
import React, { useContext, useMemo } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import { difference } from "lodash";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import styled, { useTheme } from "styled-components";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { isSymbol } from "@shared/types/utils.ts";

interface SignalLeverageWarningModalProps {
  pendingLeverage: number | undefined;
  setPendingLeverage: (pendingLeverage: number | undefined) => void;
  getInvalidLeverageSelectedExchangesData: (
    leverage?: number
  ) => Record<string, number>;
  setMultiplier: (leverage: number) => void;
}

const SignalLeverageWarningModal = ({
  pendingLeverage,
  setPendingLeverage,
  getInvalidLeverageSelectedExchangesData,
  setMultiplier,
}: SignalLeverageWarningModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const theme = useTheme();
  const tradingTerminalData = useContext(TradingTerminalContext);
  const botFormData = useContext(BotFormContext);
  const { exchanges, setExchanges } = tradingTerminalData ?? botFormData ?? {};

  const invalidSelectedLeverageExchangesData = useMemo(() => {
    return getInvalidLeverageSelectedExchangesData(pendingLeverage);
  }, [pendingLeverage, getInvalidLeverageSelectedExchangesData]);

  return (
    <Modal
      open={!!pendingLeverage}
      onCancel={() => setPendingLeverage(undefined)}
      onOk={() => {
        if (pendingLeverage) {
          setMultiplier(pendingLeverage);
        }
        if (!isSymbol(exchanges)) {
          setExchanges?.(
            difference(
              exchanges,
              Object.keys(invalidSelectedLeverageExchangesData || {})
            )
          );
        }
        setPendingLeverage(undefined);
      }}
      title={t("modals.leverage_exceeds_limits")}
      okText={t("buttons.apply_leverage")}
    >
      <Text>{t("modals.leverage_exceeds_limits_content")}</Text>
      <Title level={4} style={{ margin: "16px 0 4px" }}>
        {t("modals.exchanges_with_lower_limits")}
      </Title>
      {Object.entries(invalidSelectedLeverageExchangesData || {}).map(
        ([exchange, maxLeverage]) => {
          return (
            <ExchangeWrapper key={exchange}>
              <TextWithIcon
                iconType={ICON_TYPES.EXCHANGE}
                coinOrExchangeName={exchange}
                text={exchange}
                textStyle={{ color: theme.colors.primaryText }}
              />
              <Flex>
                <Text>{t("modals.max")}</Text>
                <Text style={{ marginLeft: 4 }}>{maxLeverage}x</Text>
              </Flex>
            </ExchangeWrapper>
          );
        }
      )}
    </Modal>
  );
};

export default SignalLeverageWarningModal;

const ExchangeWrapper = styled.div`
  padding: 4px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 320px;
`;
